/* Shopify THEME asset, not Customer Events pixel code. No Meta token required. */
(function () {
  'use strict';
  const KEY = 'jubi_meta_route_v1'; // Same schema as the installed custom pixel.
  const TTL = 30 * 24 * 60 * 60 * 1000;
  const names = ['jubi_meta_route', 'jubi_meta_route_expires', 'jubi_meta_fbc'];
  const routeName = value => value === 'gotjubi' || value === 'buyjubi' ? value : null;
  function valid(record, now) {
    return record && routeName(record.route) && Number.isFinite(record.expires) &&
      record.expires > now && record.expires <= now + TTL;
  }
  function select(previous, href, now, newEntry) {
    const url = new URL(href);
    const explicit = newEntry && routeName(url.searchParams.get('entry_source'));
    const stored = valid(previous, now) ? previous : null;
    const route = explicit || (stored && stored.route) || 'buyjubi';
    let fbc = stored && stored.route === route && typeof stored.fbc === 'string' &&
      /^fb\.1\.\d+\.\S{1,1000}$/.test(stored.fbc) ? stored.fbc : '';
    const click = newEntry && url.searchParams.get('fbclid');
    if (click && click.length <= 1000 && !/\s/.test(click) &&
      (!fbc || fbc.split('.').slice(3).join('.') !== click)) {
      fbc = 'fb.1.' + now + '.' + click;
    }
    return {
      route,
      expires: explicit || !stored ? now + TTL : stored.expires,
      ...(fbc ? { fbc } : {}),
    };
  }
  function attributes(record) {
    return {
      jubi_meta_route: record ? record.route : '',
      jubi_meta_route_expires: record ? String(record.expires) : '',
      jubi_meta_fbc: record && record.fbc || '',
    };
  }
  function allowed(privacy) {
    try {
      return privacy.marketingAllowed() === true &&
        privacy.analyticsProcessingAllowed() === true &&
        privacy.saleOfDataAllowed() === true;
    } catch { return false; }
  }
  // Enables offline unit tests; Shopify loads this as a normal classic script.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { select, attributes, allowed, TTL, KEY };
    return;
  }
  if (window.JubiMetaRoute) return;

  let lastEntry = '';
  let volatile = null;
  let queue = Promise.resolve();
  let resuming = false;
  let checkingOut = false;
  let privacyReady = false;
  const privacy = () => window.Shopify && window.Shopify.customerPrivacy;
  const permitted = () => allowed(privacy());
  const warn = () => console.warn('[Jubi routing] Cart route not confirmed; no event was sent by this bridge.');
  const root = () => {
    const path = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid Shopify root');
    return url.pathname.replace(/\/?$/, '/');
  };
  const removeStored = () => {
    try { localStorage.removeItem(KEY); } catch { /* No storage fallback. */ }
    volatile = null;
    lastEntry = '';
  };
  const readStored = () => {
    try { return JSON.parse(localStorage.getItem(KEY)); } catch { return volatile; }
  };
  function formAttributes(values) {
    document.querySelectorAll('form').forEach(form => {
      let action;
      try { action = new URL(form.action || location.href, location.href); } catch { return; }
      if (action.origin !== location.origin || !/\/cart\/?$/.test(action.pathname)) return;
      names.forEach(name => {
        const inputName = 'attributes[' + name + ']';
        const existing = [...form.querySelectorAll('input')].filter(input => input.name === inputName);
        if (!values[name]) {
          existing.forEach(input => input.remove());
          return;
        }
        const input = existing.shift() || document.createElement('input');
        existing.forEach(duplicate => duplicate.remove());
        input.type = 'hidden';
        input.name = inputName;
        input.value = values[name];
        if (!input.parentNode) form.appendChild(input);
      });
    });
  }
  async function writeCart(values) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 7000);
    try {
      const response = await fetch(root() + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: values }), signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();
      for (const name of names) {
        if (String(cart.attributes && cart.attributes[name] || '') !== values[name]) {
          throw new Error('Cart attribute not confirmed');
        }
      }
    } finally { clearTimeout(timeout); }
  }
  async function synchronize() {
    // Clear previous marketing selection when permission is absent; do not read it.
    if (!permitted()) {
      removeStored();
      const empty = attributes(null);
      formAttributes(empty);
      await writeCart(empty);
      return;
    }
    const url = new URL(location.href);
    const entry = url.origin + url.pathname + url.search; // Hash is not a new entry.
    const record = select(readStored(), url.href, Date.now(), entry !== lastEntry);
    lastEntry = entry;
    volatile = record;
    try { localStorage.setItem(KEY, JSON.stringify(record)); }
    catch { console.warn('[Jubi routing] Route cannot persist across visits in this browser.'); }
    const values = attributes(record);
    formAttributes(values);
    await writeCart(values);
    // Consent may have changed while the write was in flight. Clear before return.
    if (!permitted()) {
      removeStored();
      formAttributes(attributes(null));
      await writeCart(attributes(null));
    }
  }
  function sync() {
    const result = queue.catch(() => {}).then(synchronize);
    queue = result;
    return result;
  }
  function showError() {
    let notice = document.getElementById('jubi-checkout-status');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'jubi-checkout-status';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'position:fixed;bottom:16px;left:16px;right:16px;z-index:2147483647;padding:16px;background:#fff;color:#111;border:2px solid #b00020;font:16px/1.5 sans-serif';
      document.body.appendChild(notice);
    }
    notice.textContent = 'We could not prepare checkout. Please check your connection and try the checkout button again.';
  }
  async function beforeCheckout(resume) {
    if (checkingOut) return;
    checkingOut = true;
    try {
      await ready;
      await sync();
      document.getElementById('jubi-checkout-status')?.remove();
      resuming = true;
      try { resume(); } finally { resuming = false; }
    } catch {
      warn();
      // Do not proceed with a potentially stale route when marketing is enabled.
      // Denied consent must not stop shopping; Shopify must suppress its pixel.
      if (!permitted()) {
        resuming = true;
        try { resume(); } finally { resuming = false; }
      } else showError();
    } finally { checkingOut = false; }
  }
  function isCheckoutLink(element) {
    if (!element || element.tagName !== 'A') return false;
    try {
      const url = new URL(element.href, location.href);
      return url.origin === location.origin && /\/checkout\/?$/.test(url.pathname);
    } catch { return false; }
  }
  document.addEventListener('click', event => {
    if (resuming || event.defaultPrevented || event.button !== 0 ||
      event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const control = event.target.closest?.('a,button,input[type="submit"]');
    if (!control || (!isCheckoutLink(control) && control.getAttribute('name') !== 'checkout')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void beforeCheckout(() => control.click());
  }, true);
  document.addEventListener('submit', event => {
    if (resuming || event.defaultPrevented) return;
    const form = event.target;
    let action;
    try { action = new URL(form.action || location.href, location.href); } catch { return; }
    const submitter = event.submitter;
    if (action.origin !== location.origin ||
      (!/\/checkout\/?$/.test(action.pathname) && submitter?.name !== 'checkout')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void beforeCheckout(() => form.requestSubmit(submitter || undefined));
  }, true);
  const ready = new Promise(resolve => {
    let completed = false;
    const done = success => {
      if (completed) return;
      completed = true;
      privacyReady = success;
      if (!success) console.warn('[Jubi routing] Shopify privacy API unavailable; advertising route disabled.');
      resolve();
    };
    if (privacy()) { done(true); return; }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }],
        error => done(!error && !!privacy()));
    }
    setTimeout(() => done(false), 5000);
  });
  window.JubiMetaRoute = Object.freeze({
    ready,
    // Custom theme checkout handlers must await this before navigating.
    syncBeforeCheckout: async () => { await ready; await sync(); },
  });
  document.addEventListener('visitorConsentCollected', () => {
    if (privacy()) privacyReady = true;
    if (!permitted()) removeStored();
    void ready.then(sync).catch(warn);
  });
  window.addEventListener('pageshow', () => { void ready.then(sync).catch(warn); });
  window.addEventListener('popstate', () => { void ready.then(sync).catch(warn); });
  window.addEventListener('storage', event => {
    if (event.key === KEY) { volatile = null; void ready.then(sync).catch(warn); }
  });
  void ready.then(sync).catch(warn);
})();